import { baseApi } from '../../../shared/api/baseApi';
import { mapNewsHandbookItem } from './newsMappers';
import type { NewsArticle, NewsListResponse } from '../../news/model/types';
import type { NewsItem } from '../../../types/news';

const ENDPOINT = '/api/admin/news';

const TAG = 'News' as const;

export interface NewsArticleForm {
    title: string;
    description: string;
    file?: File | null;
}

function toBody(form: NewsArticleForm, withDate: boolean): FormData {
    const body = new FormData();
    body.append('title', form.title.trim());
    body.append('description', form.description);
    body.append('image_files', form.file ?? 'null');

    if (withDate) body.append('date', new Date().toLocaleDateString('ru-RU'));

    return body;
}

export const newsHandbookApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        getNewsHandbook: build.query<NewsItem[], void>({
            query: () => ({
                url: ENDPOINT,
                params: { page_size: 100, page_num: 1, query: '' },
            }),
            transformResponse: (response: NewsListResponse): NewsItem[] =>
                Array.isArray(response?.data) ? response.data.map(mapNewsHandbookItem) : [],
            providesTags: [{ type: TAG, id: 'LIST' }],
        }),

        createArticle: build.mutation<NewsArticle, NewsArticleForm>({
            query: (form) => ({ url: ENDPOINT, method: 'POST', body: toBody(form, true) }),
            invalidatesTags: [{ type: TAG, id: 'LIST' }],
        }),

        updateArticle: build.mutation<NewsArticle, { id: string; form: NewsArticleForm }>({
            query: ({ id, form }) => ({
                url: `${ENDPOINT}/${id}`,
                method: 'PATCH',
                body: toBody(form, false),
            }),
            invalidatesTags: [{ type: TAG, id: 'LIST' }],
        }),

        deleteArticle: build.mutation<unknown, string>({
            query: (id) => ({ url: `${ENDPOINT}/${id}`, method: 'DELETE' }),
            invalidatesTags: [{ type: TAG, id: 'LIST' }],
        }),
    }),
    overrideExisting: false,
});

export const {
    useGetNewsHandbookQuery,
    useCreateArticleMutation,
    useUpdateArticleMutation,
    useDeleteArticleMutation,
} = newsHandbookApi;